import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { resendInviteForTeamMember } from "api/landlord/landlord";
import { Layout } from "providers/Layout";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, ButtonSize, ButtonVariant, Checkbox } from "shared/components";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { Loader } from "shared/components/Loader";
import { useForm } from "shared/hooks";
import { Mail01, Trash02 } from "untitledui-js-base";
import { getErrorMessage } from "utils/error";
import { roleLabels } from "views/settings-users/constants";
import { User } from "views/settings-users/types";
import { RoleSelect } from "./components/RoleSelect";
import { AsyncSelect } from "shared/components/AsyncSelect/AsyncSelect";
import { loadPropertyOptions } from "shared/app/InviteApplicantsModal/api";
import { ROLE, useAuthContext } from "providers/Auth";
import { RemoveUserModal } from "views/settings-users/components/RemoveUserModal";
import { useState } from "react";
import { Card } from "shared/components/Card";

export const UserPermissionsView = () => {
  const { auth } = useAuthContext();
  const { userId } = useParams();
  const navigate = useNavigate();
  const [showRemoveModal, setShowRemoveModal] = useState(false);

  const isCurrentUser = Number(userId) === auth?.id;

  const { register, values, handleChange } = useForm({
    role: ROLE.Admin,
    can_change_rent_amount: false,
    can_change_available_by_date: false,
    can_make_decisions: false,
    can_view_reports: false,
    can_cancel_applications: false,
    can_change_unit: false,
    can_edit_income: true,
    can_override_decision_recommendation: false,
    properties: []
  });

  const queryClient = useQueryClient();

  const {
    data: user,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ["USER_DETAILS", userId],
    queryFn: () =>
      instance
        .get(`/portal/v1/users/${userId}`)
        .then(({ data }: { data: User }) => {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          handleChange("role", () => data.role || "")({});
          handleChange(
            "properties",
            () =>
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              data.properties.map((p) => ({ value: p.id, label: p.name })) || []
          )({});
          handleChange(
            "can_change_rent_amount",
            () => data.can_change_rent_amount
          )({});
          handleChange(
            "can_change_available_by_date",
            () => data.can_change_available_by_date
          )({});
          handleChange("can_make_decisions", () => data.can_make_decisions)({});
          handleChange("can_view_reports", () => data.can_view_reports)({});
          handleChange("can_edit_income", () => data.can_edit_income)({});
          handleChange(
            "can_override_decision_recommendation",
            () => data.can_override_decision_recommendation
          )({});
          handleChange(
            "can_cancel_applications",
            () => data.can_cancel_applications
          )({});
          handleChange("can_change_unit", () => data.can_change_unit)({});
          return data;
        })
  });

  const handleRoleChange = (e) => {
    const newRole = e.target.value;
    if (newRole === ROLE.Admin) {
      handleChange("role", () => ROLE.Admin)({});
      handleChange("can_change_rent_amount", () => true)({});
      handleChange("can_change_available_by_date", () => true)({});
      handleChange("can_make_decisions", () => true)({});
      handleChange("can_view_reports", () => true)({});
      handleChange("properties", () => [])({});
    }
    handleChange("role", () => newRole)(e);
  };

  const { mutate: resendInvite } = useMutation({
    mutationFn: (userId: number) => {
      return resendInviteForTeamMember({ userId });
    },
    onSuccess: () => {
      toast.success("Invite sent");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
    mutationKey: ["RESEND_INVITE"]
  });

  const { mutate: updateUserPermissions } = useMutation({
    mutationKey: ["UPDATE_PERMISSIONS"],
    mutationFn: (userId: number | string) => {
      const { properties, ...rest } = values;
      return instance.patch(`/portal/v1/users/${userId}/permissions`, {
        ...rest,
        property_ids: properties.map((p) => p.value)
      });
    },
    onSuccess: () => {
      toast.success("User permissions have been successfully updated");
      queryClient.invalidateQueries({
        queryKey: ["USER_DETAILS", user?.id]
      });
      navigate("/settings/users");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    }
  });

  const { mutate: deleteUser } = useMutation({
    mutationKey: ["DELETE_USER"],
    mutationFn: (userId: number) =>
      instance.delete(`/portal/v1/users/${userId}`),
    onSuccess: () => {
      toast.success("User was successfully removed");
      queryClient.invalidateQueries({
        queryKey: ["USER_DETAILS", user?.id]
      });
      navigate("/settings/users");
      if (isCurrentUser) window.location.reload();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    }
  });

  if (isError)
    return (
      <Layout
        header={
          <div className="flex items-center justify-between px-6 py-3">
            <BreadCrumbs
              crumbs={[
                {
                  title: "Settings",
                  href: "/settings"
                },
                {
                  title: "Users",
                  href: "/settings/users"
                }
              ]}
            />
          </div>
        }
      >
        <div>
          <h1 className="mb-4 text-lg font-medium">
            Error loading user details.
          </h1>
          <p className="text-base font-medium">{error.message}</p>
        </div>
      </Layout>
    );

  if (isLoading)
    return (
      <Layout
        header={
          <div className="flex items-center justify-between px-6 py-3">
            <BreadCrumbs
              crumbs={[
                {
                  title: "Settings",
                  href: "/settings"
                },
                {
                  title: "Users",
                  href: "/settings/users"
                }
              ]}
            />
          </div>
        }
      >
        <div className="h-full w-full flex items-center justify-center">
          <Loader />
        </div>
      </Layout>
    );
  return (
    <Layout
      header={
        <>
          <div className="flex items-center justify-between border-b border-alpha-40 px-3 md:px-6 pb-3">
            <BreadCrumbs
              crumbs={[
                {
                  title: "Settings",
                  href: "/settings"
                },
                {
                  title: "Users",
                  href: "/settings/users"
                },
                {
                  title: user?.name || "--",
                  href: `/settings/users/${userId}`
                }
              ]}
            />
          </div>
          <div className="flex items-center justify-between p-6 pb-3">
            <div>
              <h1 className="text-xl font-bold text-content-1000 mb-1">
                {user?.name}{" "}
                {isCurrentUser ? (
                  <span className="text-content-100">(you)</span>
                ) : null}
              </h1>
              <p className="text-xs text-content-500">{user?.email}</p>
            </div>
            {user?.role === ROLE.Owner ? null : (
              <ContextMenuPopover
                borderLess={false}
                actions={[
                  {
                    title: (
                      <span className="flex gap-3 items-center">
                        <Mail01 size="20" className="text-content-200" />
                        Resend invite
                      </span>
                    ),
                    onClick: () => resendInvite(Number(user?.id))
                  },
                  {
                    title: (
                      <span className="flex gap-3 items-center">
                        <Trash02 size="20" className="text-content-200" />
                        Remove user
                      </span>
                    ),
                    onClick: () => setShowRemoveModal(true)
                  }
                ]}
              />
            )}
          </div>
        </>
      }
      footer={
        user?.role === ROLE.Owner ? null : (
          <div className="flex justify-end gap-2">
            <Link to="/settings/users">
              <Button
                size={ButtonSize.BASE}
                variant={ButtonVariant.BASE}
                className="w-[125px]"
              >
                Cancel
              </Button>
            </Link>
            <Button
              size={ButtonSize.BASE}
              variant={ButtonVariant.PRIMARY}
              onClick={() => updateUserPermissions(Number(user?.id))}
              className="w-[125px]"
            >
              Save
            </Button>
          </div>
        )
      }
    >
      <Card className="max-w-[788px] shadow-xs p-6 w-full">
        <h3 className="text-base font-bold text-content-1000 mb-6">
          Permissions
        </h3>
        <RoleSelect
          role={values.role === ROLE.Owner ? "owner" : values.role}
          onChange={handleRoleChange}
          isOwner={values.role === ROLE.Owner}
        />
        <p className="mt-2 text-sm text-content-400 mb-6">
          {roleLabels[values.role]}
        </p>
        <p className="text-sm text-content-400 font-medium">Property</p>
        <div className="border-b border-alpha-40 mt-2 mb-4" />
        <div
          className={
            [ROLE.Admin, ROLE.Owner].includes(values.role) ? "opacity-40" : ""
          }
        >
          <p className="text-sm font-medium text-content-700 mb-2">
            Property access
          </p>
          <div className="mb-4">
            {[ROLE.Admin, ROLE.Owner].includes(values.role) ? (
              <p className="text-xs text-content-200 font-medium">
                All properties
              </p>
            ) : (
              <AsyncSelect
                id="property_ids"
                searchable
                hideOptional
                isMulti
                // @ts-expect-error -- meh
                loadOptions={loadPropertyOptions}
                additional={{
                  page: 1
                }}
                {...register("properties")}
                onChange={(selected) =>
                  handleChange("properties", () => selected)({})
                }
              />
            )}
          </div>
          <div className="mb-2">
            <Checkbox
              id="rent_amount"
              label="Change the rent amount"
              labelClassName="text-content-700 font-medium"
              checked={
                [ROLE.Admin, ROLE.Owner].includes(values.role) ||
                values.can_change_rent_amount
              }
              {...register("can_change_rent_amount", (v) => v.target.checked)}
              disabled={[ROLE.Admin, ROLE.Owner].includes(values.role)}
            />
          </div>
          <div className="mb-8">
            <Checkbox
              id="available_by_date"
              label="Change the available by date"
              checked={
                [ROLE.Admin, ROLE.Owner].includes(values.role) ||
                values.can_change_available_by_date
              }
              labelClassName="text-content-700 font-medium"
              disabled={[ROLE.Admin, ROLE.Owner].includes(values.role)}
              {...register(
                "can_change_available_by_date",
                (v) => v.target.checked
              )}
            />
          </div>
        </div>
        <p className="text-content-400 text-sm font-medium">Application</p>
        <div className="border-b border-alpha-40 mt-2 mb-4" />
        <div
          className={
            [ROLE.Admin, ROLE.Owner].includes(values.role) ? "opacity-40" : ""
          }
        >
          <div className="mb-2">
            <Checkbox
              id="make_decisions"
              label="Make decisions"
              checked={
                [ROLE.Admin, ROLE.Owner].includes(values.role) ||
                values.can_make_decisions
              }
              labelClassName="text-content-700 font-medium"
              disabled={[ROLE.Admin, ROLE.Owner].includes(values.role)}
              {...register("can_make_decisions", (v) => v.target.checked)}
            />
          </div>
          <div className="mb-2">
            <Checkbox
              id="view_reports"
              label="View reports"
              labelClassName="text-content-700 font-medium"
              checked={
                [ROLE.Admin, ROLE.Owner].includes(values.role) ||
                values.can_view_reports
              }
              disabled={[ROLE.Admin, ROLE.Owner].includes(values.role)}
              {...register("can_view_reports", (v) => v.target.checked)}
            />
          </div>
          <div className="mb-2">
            <Checkbox
              id="cancel_applications"
              label="Cancel application"
              labelClassName="text-content-700 font-medium"
              checked={
                [ROLE.Admin, ROLE.Owner].includes(values.role) ||
                values.can_cancel_applications
              }
              disabled={[ROLE.Admin, ROLE.Owner].includes(values.role)}
              {...register("can_cancel_applications", (v) => v.target.checked)}
            />
          </div>

          <div className="mb-2">
            <Checkbox
              id="override_decision_recommendation"
              label="Override decision recommendation"
              labelClassName="text-content-700 font-medium"
              checked={
                [ROLE.Admin, ROLE.Owner].includes(values.role) ||
                values.can_override_decision_recommendation
              }
              {...register(
                "can_override_decision_recommendation",
                (v) => v.target.checked
              )}
              disabled={[ROLE.Admin, ROLE.Owner].includes(values.role)}
            />
          </div>
          <div className="mb-2">
            <Checkbox
              id="change_unit"
              label="Move application to another unit"
              labelClassName="text-content-700 font-medium"
              checked={
                [ROLE.Admin, ROLE.Owner].includes(values.role) ||
                values.can_change_unit
              }
              disabled={[ROLE.Admin, ROLE.Owner].includes(values.role)}
              {...register("can_change_unit", (v) => v.target.checked)}
            />
          </div>
          <div className="mb-2">
            <Checkbox
              id="edit_income"
              label="Income editing"
              labelClassName="text-content-700 font-medium"
              checked={
                [ROLE.Admin, ROLE.Owner].includes(values.role) ||
                values.can_edit_income
              }
              disabled={[ROLE.Admin, ROLE.Owner].includes(values.role)}
              {...register("can_edit_income", (v) => v.target.checked)}
            />
            <p className="text-sm text-content-400 mb-6 ml-8">
              Enable this setting to allow users with the Property Manager role
              to edit income values within the Income Report. Disabling this
              setting restricts users to read-only access.
            </p>
          </div>
        </div>
      </Card>
      <RemoveUserModal
        opened={showRemoveModal}
        user={user}
        onRemove={() => deleteUser(Number(user?.id))}
        onClose={() => setShowRemoveModal(false)}
      />
    </Layout>
  );
};
